import React, { useState, useEffect, useRef } from 'react';
import {
  LinkIcon,
  QrCodeIcon,
  PrinterIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import QRCode from 'qrcode';
import { jwtDecode } from 'jwt-decode';
import { buildRestaurantFrontendOrigin, getRestaurantDetailsFromStorage } from '../utils/restaurantUtils';
import { DecodedToken } from '../types';

const StoreFrontend: React.FC = () => {
  const [storeUrl, setStoreUrl] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [restaurantDetails, setRestaurantDetails] = useState<any>(null);
  const [cardBackgroundMode, setCardBackgroundMode] = useState<'solid' | 'gradient' | 'image'>('gradient');
  const [cardColor, setCardColor] = useState('#ffffff');
  const [cardGradientFrom, setCardGradientFrom] = useState('#fff7ed');
  const [cardGradientTo, setCardGradientTo] = useState('#ffffff');
  const [cardBackgroundImage, setCardBackgroundImage] = useState('');
  const [cardBackgroundImageName, setCardBackgroundImageName] = useState<string>('');
  const [cardImageFit, setCardImageFit] = useState<'cover' | 'contain'>('cover');
  const [cardImagePosition, setCardImagePosition] = useState<'center' | 'top' | 'bottom'>('center');
  const [cardTextColor, setCardTextColor] = useState<string>('#000000'); // contact block
  const [cardNameColor, setCardNameColor] = useState<string>('#f97316');
  const [cardScanColor, setCardScanColor] = useState<string>('#ffffff'); // scan to order
  const [showAccentBar, setShowAccentBar] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showEmail, setShowEmail] = useState(true);
  const [showWebsite, setShowWebsite] = useState(true);
  const [showInstagram, setShowInstagram] = useState(true);
  const [showSecondMobile, setShowSecondMobile] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [customLogo, setCustomLogo] = useState<string>('');
  const [customLogoName, setCustomLogoName] = useState<string>('');
  const [tokenMobile, setTokenMobile] = useState<string>('');
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Initialize store URL with dynamic values
    const details = getRestaurantDetailsFromStorage();
    setRestaurantDetails(details);
    const dynamicStoreUrl = buildRestaurantFrontendOrigin(details || undefined);
    setStoreUrl(dynamicStoreUrl);

    const theme = (details as { theme?: { primary?: string; background?: string; surface?: string } })?.theme;
    const primary = theme?.primary;
    if (primary) {
      setCardNameColor(primary);
    }
    if (theme) {
      setCardBackgroundMode('gradient');
      setCardGradientFrom(theme.background ?? '#fff7ed');
      setCardGradientTo(theme.surface ?? '#ffffff');
      setCardColor(theme.surface ?? '#ffffff');
    }

    // Get mobile from token
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const decoded = jwtDecode<DecodedToken>(token);
        if (decoded?.data?.mobile) {
          setTokenMobile(decoded.data.mobile);
        }
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }, []);

  useEffect(() => {
    if (storeUrl) {
      generateQRCode();
    }
  }, [storeUrl]);

  const derivedWebsiteUrl = React.useMemo(() => {
    try {
      const host = new URL(storeUrl).hostname;
      if (host.endsWith('menunix.com')) {
        const username = host.split('.')[0];
        return username ? `https://${username}.menunix.com` : storeUrl;
      }
      return storeUrl;
    } catch (e) {
      return storeUrl;
    }
  }, [storeUrl]);

  const handleBackgroundImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCardBackgroundImage('');
      setCardBackgroundImageName('');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result?.toString() || '';
      setCardBackgroundImage(result);
      setCardBackgroundImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCustomLogo('');
      setCustomLogoName('');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result?.toString() || '';
      setCustomLogo(result);
      setCustomLogoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const generateQRCode = async () => {
    setIsGeneratingQR(true);
    try {
      const qrDataUrl = await QRCode.toDataURL(storeUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1f2937',
          light: '#ffffff'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareUrl = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Visit My Store',
          text: 'Check out my online store!',
          url: storeUrl,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copy
      copyToClipboard();
    }
  };

  const getCardBackgroundCss = () => {
    if (cardBackgroundMode === 'gradient') {
      return {
        background: `linear-gradient(135deg, ${cardGradientFrom}, ${cardGradientTo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    if (cardBackgroundMode === 'image' && cardBackgroundImage.trim()) {
      return {
        backgroundImage: `url('${cardBackgroundImage.replace(/'/g, "\\'")}')`,
        backgroundSize: cardImageFit,
        backgroundPosition: cardImagePosition,
      };
    }
    return {
      background: cardColor || '#ffffff',
    };
  };

  const printVisitingCard = () => {
    if (!qrCodeDataUrl) return;
    
    const restaurantName = restaurantDetails?.name || 'Restaurant';
    const theme = restaurantDetails?.theme;
    const primaryColor = theme?.primary || '#f97316';
    const contactColor = cardTextColor || primaryColor;
    const nameColor = cardNameColor || primaryColor;
    const scanColor = cardScanColor || primaryColor;
    const cardBgCss = getCardBackgroundCss();
    const cardBackgroundCssText = `
      ${cardBgCss.background ? `background:${cardBgCss.background};` : ''}
      ${cardBgCss.backgroundImage ? `background-image:${cardBgCss.backgroundImage};` : ''}
      ${cardBgCss.backgroundSize ? `background-size:${cardBgCss.backgroundSize};` : ''}
      ${cardBgCss.backgroundPosition ? `background-position:${cardBgCss.backgroundPosition};` : ''}
    `;
    
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const cardWidthIn = 3.5;
    const cardHeightIn = 2;
    const restaurantAddress = restaurantDetails?.address || '';
    const restaurantMobile = restaurantDetails?.phone || (restaurantDetails as any)?.mobile || '';
    const restaurantEmail = (restaurantDetails as any)?.email || '';
    const restaurantInstagram = restaurantDetails?.socialMedia?.instagram || '';
    const logoUrl = restaurantDetails?.logo || (restaurantDetails as any)?.logo || '';
    const effectiveLogoUrl = customLogo || logoUrl;
    
    // Extract FSSAI from description (format: "FSSAI License No. XXXXXX")
    let fssaiNumber = '';
    if (restaurantDetails?.description) {
      const desc = restaurantDetails.description;
      if (desc.startsWith('FSSAI License No. ')) {
        fssaiNumber = desc.replace('FSSAI License No. ', '').trim();
      } else if (desc.trim()) {
        fssaiNumber = desc.trim();
      }
    }
    
    // Generate HTML — 3.5" x 2" visiting card: logo (if exists), name, address, mobile, Scan to order QR; professional layout
    const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Visiting Card</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                margin: 0; padding: 0; font-size: 10px; line-height: 1.35; color: #1f2937;
                background: #f8fafc; width: ${cardWidthIn}in; height: ${cardHeightIn}in; overflow: hidden;
              }
              .page { width: 100%; height: 100%; display: flex; }
              .card {
                width: 100%; height: 100%;
                border: 1px solid #e5e7eb; ${showAccentBar ? `border-left: 5px solid ${primaryColor};` : 'border-left: 1px solid #e5e7eb;'}
                border-radius: 0; padding: 0.18in; display: flex; flex-direction: column; box-sizing: border-box;
                ${cardBackgroundCssText}
              }
              .card-header {
                display: flex; align-items: flex-start; gap: 8px; flex-shrink: 0;
                padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb;
              }
              .card-logo {
                width: 0.28in; height: 0.28in; border-radius: 6px; object-fit: contain;
                flex-shrink: 0; border: 1px solid #f3f4f6;
              }
              .card-logo[data-empty="1"] { display: none; }
              .restaurant-name {
                flex: 1; min-width: 0; font-size: 13px; font-weight: 700; line-height: 1.2;
                color: ${nameColor}; text-transform: uppercase; letter-spacing: 0.04em;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                background: #ffffff; padding: 4px 6px; border-radius: 6px;
              }
              .fssai-number {
                font-size: 7px; color: ${contactColor}; font-weight: 600;
                margin-top: 2px; white-space: nowrap;
              }
              .card-body { flex: 1; display: flex; gap: 12px; min-height: 0; align-items: center; }
              .card-info { flex: 1; min-width: 0; font-size: 8px; color: ${contactColor}; line-height: 1.4; font-weight: 600; }
              .card-info .lbl { font-weight: 700; color: ${contactColor}; display: inline-flex; align-items: center; vertical-align: middle; }
              .card-address { word-break: break-word; margin-bottom: 5px; }
              .card-mobile { white-space: nowrap; }
              .card-email { white-space: nowrap; }
              .card-website { white-space: nowrap; }
              .card-instagram { white-space: nowrap; margin-left: 1px; display: flex; flex-wrap: nowrap; align-items: center; gap: 2px; }
              .card-instagram .lbl { font-weight: 700; color: #E4405F; display: inline-flex; align-items: center; flex-shrink: 0; line-height: 1; margin-left: 0.5px; }
              .card-instagram .lbl svg { display: block; width: 8px; height: 8px; margin-right: 2px; flex-shrink: 0; }
              .card-instagram .card-instagram-txt { flex-shrink: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
              .card-qr { flex-shrink: 0; display: flex; flex-direction: column; align-items: center; }
              .qr-wrap {
                width: 0.9in; height: 0.9in; padding: 5px; background: #ffffff;
                border-radius: 6px; border: 1px solid #e5e7eb; box-sizing: border-box;
              }
              .qr-wrap img { width: 100%; height: 100%; display: block; object-fit: contain; border-radius: 4px; }
              .scan-label { font-size: 8px; font-weight: 700; color: ${scanColor}; margin-top: 5px; letter-spacing: 0.03em; }
              @media print {
                @page { size: ${cardWidthIn}in ${cardHeightIn}in; margin: 0; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                body { background: #fff !important; }
                .card { border: 1px solid #e5e7eb !important; ${showAccentBar ? `border-left: 5px solid ${primaryColor} !important;` : 'border-left: 1px solid #e5e7eb !important;'} }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <div class="card">
                <div class="card-header">
                  ${showLogo && effectiveLogoUrl ? `<img src="${effectiveLogoUrl.replace(/"/g, '&quot;')}" class="card-logo" alt="" onerror="this.setAttribute('data-empty','1')" />` : ''}
                  <div style="flex: 1; min-width: 0;">
                    <div class="restaurant-name">${restaurantName}</div>
                    ${fssaiNumber ? `<div class="fssai-number">FSSAI: ${fssaiNumber}</div>` : ''}
                  </div>
                </div>
                <div class="card-body">
                  <div class="card-info">
                    ${showAddress && restaurantAddress ? `<div class="card-address"><span class="lbl">📍</span> ${restaurantAddress}</div>` : ''}
                    ${showPhone && restaurantMobile ? `<div class="card-mobile"><span class="lbl">📞</span> ${restaurantMobile}</div>` : ''}
                    ${showSecondMobile && tokenMobile ? `<div class="card-mobile"><span class="lbl">📱</span> ${tokenMobile}</div>` : ''}
                    ${showEmail && restaurantEmail ? `<div class="card-email"><span class="lbl">✉️</span> ${restaurantEmail}</div>` : ''}
                    ${showWebsite && derivedWebsiteUrl ? `<div class="card-website"><span class="lbl">🌐</span> ${derivedWebsiteUrl}</div>` : ''}
                    ${showInstagram && restaurantInstagram ? `<div class="card-instagram"><span class="lbl"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block; width: 8px; height: 8px; margin-right: 2px;"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg></span><span class="card-instagram-txt">${restaurantInstagram}</span></div>` : ''}
                  </div>
                  <div class="card-qr">
                    <div class="qr-wrap"><img src="${qrCodeDataUrl}" alt="QR" /></div>
                    <div class="scan-label">Scan to order</div>
                  </div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `;
    
    if (isMobile) {
      // For mobile: use iframe approach which works better with mobile browsers
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printContent);
        iframeDoc.close();
        
        // Wait for content to load
        iframe.onload = () => {
          setTimeout(() => {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
            // Remove iframe after printing
            setTimeout(() => {
              if (iframe.parentNode) {
                document.body.removeChild(iframe);
              }
            }, 1000);
          }, 500);
        };
        
        // Fallback: trigger print even if onload doesn't fire
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }, 1000);
      }
    } else {
      // For desktop: use popup window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Close window after print dialog is dismissed
            printWindow.onafterprint = () => {
              printWindow.close();
            };
            // Fallback: close after a delay if afterprint doesn't fire
            setTimeout(() => {
              if (!printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 250);
        };
      }
    }
  };

  const printQRCodeOnly = () => {
    if (!qrCodeDataUrl) return;
    
    const restaurantName = restaurantDetails?.name || 'Restaurant';
    const theme = restaurantDetails?.theme;
    const primaryColor = theme?.primary || '#f97316';
    const textSecondaryColor = theme?.textSecondary || '#4b5563';
    
    const urlDisplay = storeUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const cardWidthMm = 105;
    const cardHeightMm = 148;
    
    const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>&nbsp;</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                margin: 0; padding: 15mm; font-size: 12px; line-height: 1.5; color: #333;
                background: #f5f5f5; width: ${cardWidthMm}mm; height: ${cardHeightMm}mm; overflow: hidden;
              }
              .page {
                width: ${cardWidthMm}mm; height: ${cardHeightMm}mm; margin: 0 auto;
                display: flex; align-items: center; justify-content: center; position: relative;
              }
              .card {
                width: 100%; height: 100%; max-width: 100%; max-height: 100%;
                background: #ffffff; border-radius: 16px; padding: 8mm;
                box-shadow: 0 8px 24px rgba(0,0,0,0.12); box-sizing: border-box;
              }
              .card-inner {
                width: 100%; height: 100%; border-radius: 12px; overflow: hidden;
                border: 2px solid ${primaryColor}; display: flex; flex-direction: column;
                box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05); box-sizing: border-box;
              }
              .header {
                background: ${primaryColor}; color: #ffffff; padding: 20px 16px; text-align: center;
                flex-shrink: 0; box-shadow: 0 2px 8px rgba(0,0,0,0.1); min-height: 80px;
                display: flex; flex-direction: column; justify-content: center;
              }
              .restaurant-name {
                font-size: 22px; font-weight: 700; margin-bottom: 8px; letter-spacing: -0.3px;
                line-height: 1.2; text-transform: uppercase; white-space: nowrap;
                overflow: hidden; text-overflow: ellipsis;
              }
              .subtitle {
                font-size: 13px; opacity: 0.92; font-weight: 400; letter-spacing: 0.2px;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
              }
              .body {
                flex: 1; padding: 24px 20px 20px; display: flex; flex-direction: column;
                align-items: center; justify-content: space-between; position: relative;
                background: #ffffff; min-height: 0; box-sizing: border-box;
              }
              .qr-container {
                flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
                width: 100%; min-height: 0;
              }
              .qr-code {
                width: 200px; height: 200px; max-width: 200px; max-height: 200px;
                margin: 0 auto 20px; flex-shrink: 0; padding: 12px; background: #ffffff;
                border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); box-sizing: border-box;
              }
              .qr-code img { width: 100%; height: 100%; display: block; border-radius: 4px; object-fit: contain; }
              .instructions {
                font-size: 11px; color: ${textSecondaryColor}; text-align: center; letter-spacing: 0.2px;
                font-weight: 500; margin-bottom: 16px; line-height: 1.4; flex-shrink: 0;
              }
              .url-footer {
                width: 100%; text-align: center; font-size: 9px; color: ${textSecondaryColor};
                font-weight: 400; padding: 12px 8px; background: #f9fafb; border-radius: 6px;
                border: 1px solid #e5e7eb; word-break: break-all; line-height: 1.3;
                flex-shrink: 0; box-sizing: border-box;
              }
              @media print {
                @page { size: A4; margin: 0; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                body { background: #ffffff !important; padding: 0 !important; margin: 0 !important; width: ${cardWidthMm}mm; height: ${cardHeightMm}mm; }
                .page { margin: 0 !important; width: ${cardWidthMm}mm !important; height: ${cardHeightMm}mm !important; }
                .card { box-shadow: none !important; padding: 8mm !important; border-radius: 16px !important; width: 100% !important; height: 100% !important; }
                .card-inner { border-radius: 12px !important; border: 2px solid ${primaryColor} !important; }
                .header { background: ${primaryColor} !important; color: #ffffff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                .qr-code { background: #ffffff !important; box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
                .url-footer { background: #f9fafb !important; border: 1px solid #e5e7eb !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <div class="card">
                <div class="card-inner">
                  <div class="header">
                    <div class="restaurant-name">${restaurantName}</div>
                    <div class="subtitle">Scan to view our menu</div>
                  </div>
                  <div class="body">
                    <div class="qr-container">
                      <div class="qr-code">
                        <img src="${qrCodeDataUrl}" alt="QR Code" />
                      </div>
                      <div class="instructions">📱 Scan this QR code with your phone camera</div>
                    </div>
                    <div class="url-footer">🔗 https://${urlDisplay}</div>
                  </div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `;
    
    if (isMobile) {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printContent);
        iframeDoc.close();
        
        iframe.onload = () => {
          setTimeout(() => {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
            setTimeout(() => {
              if (iframe.parentNode) {
                document.body.removeChild(iframe);
              }
            }, 1000);
          }, 500);
        };
        
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }, 1000);
      }
    } else {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
              printWindow.close();
            };
            setTimeout(() => {
              if (!printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 250);
        };
      }
    }
  };

  const downloadQRCodeImage = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = 'store-qr-code.png';
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900"></h1>
        <div className="flex items-center space-x-2">
          <GlobeAltIcon className="h-5 w-5 text-orange-600" />
          <span className="text-sm text-gray-600">Share your store URL with customers</span>
        </div>
      </div>

      {/* Store URL Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center mb-4">
          <LinkIcon className="h-6 w-6 text-orange-600 mr-3" />
          <h2 className="text-lg font-semibold text-gray-900">Store URL</h2>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 sm:mr-4">
              <p className="text-sm text-gray-600 mb-1">Your store is live at:</p>
              <a 
                href={storeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-lg font-mono text-orange-600 break-all hover:text-orange-700 hover:underline cursor-pointer transition-colors"
              >
                {storeUrl}
              </a>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-md hover:from-orange-600 hover:to-pink-700 transition-colors"
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={shareUrl}
                className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <ShareIcon className="h-4 w-4 mr-1" />
                Share
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center p-3 bg-orange-50 rounded-lg">
            <ComputerDesktopIcon className="h-8 w-8 text-orange-600 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900">Desktop Optimized</p>
              <p className="text-sm text-gray-600">Perfect for browsing</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-green-50 rounded-lg">
            <DevicePhoneMobileIcon className="h-8 w-8 text-green-600 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900">Mobile Friendly</p>
              <p className="text-sm text-gray-600">Responsive design</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-purple-50 rounded-lg sm:col-span-2 lg:col-span-1">
            <GlobeAltIcon className="h-8 w-8 text-purple-600 mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900">SEO Ready</p>
              <p className="text-sm text-gray-600">Search optimized</p>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center mb-4">
          <QrCodeIcon className="h-6 w-6 text-purple-600 mr-3" />
          <h2 className="text-lg font-semibold text-gray-900">QR Code</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="text-center">
            <div className="bg-gray-50 rounded-lg p-8 mb-4 inline-block">
              {isGeneratingQR ? (
                <div className="w-64 h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt="Store QR Code"
                  className="w-64 h-64"
                />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center text-gray-400">
                  <QrCodeIcon className="h-16 w-16" />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Customers can scan this QR code to visit your store instantly
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={downloadQRCodeImage}
                disabled={!qrCodeDataUrl}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download
              </button>
              <button
                onClick={printQRCodeOnly}
                disabled={!qrCodeDataUrl}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">How to use QR Code</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                    1
                  </div>
                  <p className="text-gray-700">Print the QR code and place it in your physical store, business cards, or flyers</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                    2
                  </div>
                  <p className="text-gray-700">Customers scan the code with their phone camera</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                    3
                  </div>
                  <p className="text-gray-700">They're instantly taken to your online store</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-medium text-purple-900 mb-2">💡 Pro Tips</h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Place QR codes at eye level for easy scanning</li>
                <li>• Include a call-to-action like "Scan to shop online"</li>
                <li>• Test the QR code before printing to ensure it works</li>
                <li>• Use high-quality printing for better scan reliability</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">📱 Compatible with</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                <div>• iPhone Camera</div>
                <div>• Android Camera</div>
                <div>• WhatsApp Scanner</div>
                <div>• Google Lens</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant Visiting Card — 3.5" x 2" with logo (if exists), name, address, mobile, Scan to order QR; professional design */}
      <div className="bg-white rounded-lg shadow-sm p-6 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-purple-50 opacity-80 pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative">
          <div className="flex items-center mb-4">
            <QrCodeIcon className="h-6 w-6 text-orange-600 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Restaurant Visiting Card</h2>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <p className="text-sm text-gray-700">
              3.5″ × 2″ — Logo, name, address, mobile, Scan to order QR. Print a ready-to-use business card.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={printVisitingCard}
                disabled={!qrCodeDataUrl}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-md hover:from-orange-600 hover:to-pink-700 disabled:opacity-50 transition-colors"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print visiting card
              </button>
            </div>
          </div>

          {/* Card design */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-4">
            <h4 className="font-medium text-gray-900">Card design</h4>
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
              <div className="flex gap-3">
                {(['solid', 'gradient', 'image'] as const).map((mode) => (
                  <label key={mode} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="card-bg-mode"
                      value={mode}
                      checked={cardBackgroundMode === mode}
                      onChange={() => setCardBackgroundMode(mode)}
                      className="text-orange-600"
                    />
                    {mode === 'solid' && 'Color'}
                    {mode === 'gradient' && 'Gradient'}
                    {mode === 'image' && 'Image'}
                  </label>
                ))}
              </div>

              {cardBackgroundMode === 'solid' && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-20">Color</span>
                  <input
                    type="color"
                    value={cardColor}
                    onChange={(e) => setCardColor(e.target.value)}
                    className="h-9 w-12 p-0 border border-gray-200 rounded"
                  />
                </div>
              )}

              {cardBackgroundMode === 'gradient' && (
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <span className="w-20">From</span>
                  <input
                    type="color"
                    value={cardGradientFrom}
                    onChange={(e) => setCardGradientFrom(e.target.value)}
                    className="h-9 w-12 p-0 border border-gray-200 rounded"
                  />
                  <span className="w-12 text-center text-gray-500">to</span>
                  <input
                    type="color"
                    value={cardGradientTo}
                    onChange={(e) => setCardGradientTo(e.target.value)}
                    className="h-9 w-12 p-0 border border-gray-200 rounded"
                  />
                </div>
              )}

              {cardBackgroundMode === 'image' && (
                <div className="flex flex-col gap-3 text-sm text-gray-700 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="w-24">Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundImageUpload}
                      className="text-sm"
                    />
                    {cardBackgroundImage && (
                      <button
                        type="button"
                        onClick={() => { setCardBackgroundImage(''); setCardBackgroundImageName(''); }}
                        className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="w-24">or Image URL</span>
                    <input
                      type="url"
                      value={cardBackgroundImage}
                      onChange={(e) => { setCardBackgroundImage(e.target.value); setCardBackgroundImageName(''); }}
                      placeholder="https://example.com/background.jpg"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {cardBackgroundImageName ? `Selected: ${cardBackgroundImageName}` : 'Used as cover; images stay on your device'}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="w-24">Image fit</span>
                      <select
                        value={cardImageFit}
                        onChange={(e) => setCardImageFit(e.target.value as 'cover' | 'contain')}
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                      >
                        <option value="cover">Fill card (crop)</option>
                        <option value="contain">Fit inside (no crop)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-24">Focus</span>
                      <select
                        value={cardImagePosition}
                        onChange={(e) => setCardImagePosition(e.target.value as 'center' | 'top' | 'bottom')}
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                      >
                        <option value="center">Center</option>
                        <option value="top">Top</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Text colors */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <span className="w-28">Name color</span>
                <input
                  type="color"
                  value={cardNameColor}
                  onChange={(e) => setCardNameColor(e.target.value)}
                  className="h-9 w-12 p-0 border border-gray-200 rounded bg-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="w-28">Contact color</span>
                <input
                  type="color"
                  value={cardTextColor}
                  onChange={(e) => setCardTextColor(e.target.value)}
                  className="h-9 w-12 p-0 border border-gray-200 rounded bg-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="w-28">Scan text</span>
                <input
                  type="color"
                  value={cardScanColor}
                  onChange={(e) => setCardScanColor(e.target.value)}
                  className="h-9 w-12 p-0 border border-gray-200 rounded bg-white"
                />
              </div>
            </div>

            {/* Logo options */}
            <div className="flex flex-col gap-2 text-sm text-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showLogo}
                    onChange={(e) => setShowLogo(e.target.checked)}
                    className="text-orange-600"
                  />
                  Show logo
                </label>
                <div className="flex items-center gap-2">
                  <span className="w-24 sm:w-auto sm:min-w-[4.5rem]">Custom logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="text-xs sm:text-sm"
                  />
                  {customLogo && (
                    <button
                      type="button"
                      onClick={() => { setCustomLogo(''); setCustomLogoName(''); }}
                      className="px-2.5 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs sm:text-sm"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {customLogoName && (
                <div className="text-xs text-gray-500">
                  Selected logo: {customLogoName}
                </div>
              )}
            </div>

            {/* Show/hide fields */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-gray-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showAccentBar}
                  onChange={(e) => setShowAccentBar(e.target.checked)}
                  className="text-orange-600"
                />
                Show left accent bar
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showAddress}
                  onChange={(e) => setShowAddress(e.target.checked)}
                  className="text-orange-600"
                />
                Show address
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showPhone}
                  onChange={(e) => setShowPhone(e.target.checked)}
                  className="text-orange-600"
                />
                Show phone
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showEmail}
                  onChange={(e) => setShowEmail(e.target.checked)}
                  className="text-orange-600"
                />
                Show email
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showWebsite}
                  onChange={(e) => setShowWebsite(e.target.checked)}
                  className="text-orange-600"
                />
                Show website
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showInstagram}
                  onChange={(e) => setShowInstagram(e.target.checked)}
                  className="text-orange-600"
                />
                Show Instagram
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showSecondMobile}
                  onChange={(e) => setShowSecondMobile(e.target.checked)}
                  className="text-orange-600"
                />
                Show second mobile
              </label>
            </div>
          </div>

          <div className="flex justify-center">
            <div
              className="overflow-hidden flex flex-col relative"
              style={{
                width: '336px',
                height: '192px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                borderLeft: showAccentBar ? `5px solid ${restaurantDetails?.theme?.primary || '#f97316'}` : '1px solid #e5e7eb',
                borderRadius: 0,
                padding: '0.18in',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                ...(cardBackgroundMode === 'gradient'
                  ? { background: `linear-gradient(135deg, ${cardGradientFrom}, ${cardGradientTo})` }
                  : cardBackgroundMode === 'image' && cardBackgroundImage
                    ? {
                        backgroundImage: `url('${cardBackgroundImage}')`,
                        backgroundSize: cardImageFit,
                        backgroundPosition: cardImagePosition,
                      }
                    : { background: cardColor || '#ffffff' }),
              }}
            >
              {/* Header: logo + name */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexShrink: 0, paddingBottom: '8px', marginBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
                {showLogo && (customLogo || restaurantDetails?.logo || (restaurantDetails as any)?.logo) && (
                  <img
                    src={customLogo || restaurantDetails?.logo || (restaurantDetails as any)?.logo}
                    alt=""
                    style={{ width: '0.28in', height: '0.28in', borderRadius: '6px', objectFit: 'contain', flexShrink: 0, border: '1px solid #f3f4f6' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      lineHeight: '1.2',
                      color: cardNameColor || restaurantDetails?.theme?.primary || '#f97316',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      background: '#ffffff',
                      padding: '4px 6px',
                      borderRadius: '6px',
                    }}
                  >
                    {restaurantDetails?.name || 'Restaurant'}
                  </div>
                  {(() => {
                    let fssaiNumber = '';
                    if (restaurantDetails?.description) {
                      const desc = restaurantDetails.description;
                      if (desc.startsWith('FSSAI License No. ')) {
                        fssaiNumber = desc.replace('FSSAI License No. ', '').trim();
                      } else if (desc.trim()) {
                        fssaiNumber = desc.trim();
                      }
                    }
                    return fssaiNumber ? (
                      <div
                        style={{
                          fontSize: '7px',
                          color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316',
                          fontWeight: '600',
                          marginTop: '2px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        FSSAI: {fssaiNumber}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
              {/* Body: info | QR */}
              <div style={{ flex: 1, display: 'flex', gap: '12px', minHeight: 0, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: '8px', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316', lineHeight: '1.4', fontWeight: '600' }}>
                  {showAddress && restaurantDetails?.address && (
                    <div style={{ wordBreak: 'break-word', marginBottom: '5px' }}>
                      <span style={{ fontWeight: '700', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316' }}>📍</span> {restaurantDetails.address}
                    </div>
                  )}
                  {showPhone && (restaurantDetails?.phone || (restaurantDetails as any)?.mobile) && (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '700', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316' }}>📞</span> {restaurantDetails?.phone || (restaurantDetails as any)?.mobile}
                    </div>
                  )}
                  {showSecondMobile && tokenMobile && (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '700', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316' }}>📱</span> {tokenMobile}
                    </div>
                  )}
                  {showEmail && (restaurantDetails as any)?.email && (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '700', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316' }}>✉️</span> {(restaurantDetails as any)?.email}
                    </div>
                  )}
                  {showWebsite && derivedWebsiteUrl && (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '700', color: cardTextColor || restaurantDetails?.theme?.primary || '#f97316' }}>🌐</span> {derivedWebsiteUrl}
                    </div>
                  )}
                  {showInstagram && restaurantDetails?.socialMedia?.instagram && (
                    <div style={{ whiteSpace: 'nowrap', marginLeft: '1px', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '2px' }}>
                      <span style={{ fontWeight: '700', color: '#E4405F', display: 'inline-flex', alignItems: 'center', flexShrink: 0, lineHeight: 1, marginLeft: '0.5px' }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', width: '8px', height: '8px', marginRight: '2px' }}>
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                        </svg>
                      </span>
                      <span style={{ flexShrink: 0 }}>{restaurantDetails.socialMedia.instagram}</span>
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {qrCodeDataUrl ? (
                    <div style={{ width: '0.9in', height: '0.9in', padding: '5px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb', boxSizing: 'border-box' }}>
                      <img src={qrCodeDataUrl} alt="QR Code" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', borderRadius: '4px' }} />
                    </div>
                  ) : (
                    <div style={{ width: '0.9in', height: '0.9in', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                      {isGeneratingQR ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent" />
                      ) : (
                        <QrCodeIcon className="h-7 w-7 text-gray-400" />
                      )}
                    </div>
                  )}
                  <span
                    style={{
                      fontSize: '8px',
                      fontWeight: '700',
                      color: cardScanColor || restaurantDetails?.theme?.primary || '#f97316',
                      marginTop: '5px',
                      letterSpacing: '0.03em',
                    }}
                  >
                    Scan to order
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print template */}
      <div ref={printRef} className="hidden">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold mb-4">My Online Store</h1>
          <p className="text-gray-600 mb-6">Scan to visit our store</p>
          {qrCodeDataUrl && (
            <img src={qrCodeDataUrl} alt="QR Code" className="mx-auto mb-4" style={{ width: '200px', height: '200px' }} />
          )}
          <p className="text-sm text-gray-600 break-all">{storeUrl}</p>
          <p className="text-xs text-gray-500 mt-4">Scan this QR code with your phone camera</p>
        </div>
      </div>
    </div>
  );
};

export default StoreFrontend;